<template>
  <div class="-mx-2 flex pt-5">
    <div class="relative z-20 flex flex-col gap-6">
      <div class="flex flex-wrap">
        <!-- First Name -->
        <div class="relative z-0 mt-6 w-full px-2 sm:mt-0 lg:w-1/3">
          <BaseFormInput
            name="first_name"
            :value="patient.first_name"
            :label="$t('auth.first_name')"
            :any-dirty="patientV$.$anyDirty"
            :errors="errors"
            :is-required="true"
            @update:value="updatePatientValue('first_name', $event)"
          />
        </div>

        <!-- Last Name -->
        <div
          class="relative z-0 mt-6 w-full px-2 sm:mt-0"
          :class="isAdult ? 'lg:w-2/3' : 'lg:w-1/3'"
        >
          <BaseFormInput
            name="last_name"
            :value="patient.last_name"
            :label="$t('auth.last_name')"
            :any-dirty="patientV$.$anyDirty"
            :errors="errors"
            :is-required="true"
            @update:value="updatePatientValue('last_name', $event)"
          />
        </div>

        <!-- Pesel -->
        <div
          class="relative z-0 mt-6 w-full px-2 sm:mt-0"
          :class="isAdult ? 'lg:w-1/3' : 'order-2 lg:w-1/3'"
        >
          <BaseFormInput
            name="pesel"
            label="Pesel"
            :value="patient.pesel"
            :disabled="patient.isNoPesel"
            :any-dirty="patientV$.$anyDirty"
            :errors="errors"
            :is-required="!patient.isNoPesel"
            type="number"
            class="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            @update:value="updatePatientValue('pesel', $event)"
          />

          <BaseCheckbox
            :id="`isNoPesel-${patient.id}`"
            name="pesel"
            theme="square"
            :checked="patient.isNoPesel"
            class="mb-5 leading-none"
            @change="(checked) => updatePatientValue('isNoPesel', checked)"
          >
            <span
              class="cursor-pointer text-sm font-medium leading-none text-darkestGray"
            >
              {{ $t('auth.checkboxes.no_pesel_number') }}
            </span>
          </BaseCheckbox>
        </div>

        <!-- Phone Number for Adults -->
        <div v-if="isAdult" class="z-1 relative mb-6 w-full px-2 lg:w-2/3">
          <BasePhoneNumberInput
            name="phone_number"
            :phone-number="patient.phone_number"
            :country="patient.country"
            :errors="errors"
            :is-required="true"
            @update="onUpdatePhoneNumber($event)"
          />
        </div>

        <!-- Birth Date and Sex for Adults with No Pesel -->
        <div
          v-if="patient.isNoPesel && isAdult"
          class="mt-3 flex w-full flex-wrap"
        >
          <div class="w-full px-2 lg:w-1/2">
            <BaseDatePicker
              :id="`patient-date-picker-${index}`"
              :value="patient.birth_date"
              name="birth_date"
              :label="$t('patients.enter_date_of_birth')"
              :any-dirty="patientV$.$anyDirty"
              :errors="errors"
              :is-required="true"
              :max="maxBirthDate"
              @input="updatePatientValue('birth_date', $event)"
            />
          </div>

          <div class="w-full px-2 lg:w-1/2">
            <BaseSelect
              name="sex"
              :model-value="patient.sex"
              :label="$t('auth.gender.title')"
              :options="sexOptions"
              :is-required="true"
              :errors="errors"
              :any-dirty="patientV$.$anyDirty"
              @update:model-value="updatePatientValue('sex', $event)"
            />
          </div>
        </div>

        <!-- Accept Data Processing and Terms for Adults -->
        <div
          v-if="!hasPatients && isAdult"
          class="relative z-0 mt-6 w-full px-2 sm:mt-0"
        >
          <BaseCheckbox
            id="accept_data_processing"
            name="accept_data_processing"
            :checked="patient.accept_data_processing ?? false"
            :required="true"
            theme="square"
            :is-expandable="true"
            :errors="errors"
            class="text-sm"
            @change="
              (checked) => updatePatientValue('accept_data_processing', checked)
            "
          >
            <template #default>
              {{ $t('auth.checkboxes.accept_terms1') }}
            </template>
            <template #expandLabel>
              {{ $t('auth.checkboxes.accept_terms1_expanded') }}
              <a
                href="/obowiazek-informacyjny"
                target="_blank"
                rel="noopener noreferrer nofollow"
              >
                <span
                  class="transition-ease-in-out hover:text-primary-60 underline"
                  >{{ $t('auth.checkboxes.accept_terms1_link') }}</span
                >
              </a>
            </template>
          </BaseCheckbox>
        </div>

        <div
          v-if="!hasPatients && isAdult"
          class="relative z-0 mt-6 w-full px-2 sm:mt-0"
        >
          <BaseCheckbox
            id="accept_terms"
            name="accept_terms"
            :checked="patient.accept_terms ?? false"
            :required="true"
            theme="square"
            :errors="errors"
            class="text-sm"
            @change="(checked) => updatePatientValue('accept_terms', checked)"
          >
            {{ $t('auth.checkboxes.accept_terms2_expanded') }}
            <a
              href="/static/regulamin"
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              <span
                class="transition-ease-in-out hover:text-primary-60 underline"
              >
                {{ $t('auth.checkboxes.accept_terms2_link') }}.</span
              >
            </a>
          </BaseCheckbox>
        </div>
      </div>

      <div
        v-show="patient.isNoPesel && !isAdult"
        class="mt-3 flex w-full flex-wrap"
      >
        <div class="w-full px-2 lg:w-3/5">
          <BaseDatePicker
            :id="`patient-date-picker-${index}`"
            :value="patient.birth_date"
            name="birth_date"
            :label="$t('patients.enter_date_of_birth')"
            :any-dirty="patientV$.$anyDirty"
            :errors="errors"
            :is-required="true"
            :min="minBirthDate"
            :max="maxBirthDate"
            @input="updatePatientValue('birth_date', $event)"
          />
        </div>

        <div class="w-full px-2 lg:w-2/5">
          <BaseSelect
            name="sex"
            :model-value="patient.sex"
            :label="$t('auth.gender.title')"
            :options="sexOptions"
            :is-required="true"
            :errors="errors"
            :any-dirty="patientV$.$anyDirty"
            @update:model-value="updatePatientValue('sex', $event)"
          />
        </div>
      </div>

      <!-- Legal Guardian Declaration for kids -->
      <div v-if="!isAdult" class="relative z-0 mt-6 w-full px-2 sm:mt-0">
        <BaseCheckbox
          id="is_legal_guardian_declaration"
          name="is_legal_guardian_declaration"
          :checked="patient.is_legal_guardian_declaration ?? false"
          :required="true"
          theme="square"
          :errors="errors"
          class="text-sm"
          @change="
            (checked) =>
              updatePatientValue('is_legal_guardian_declaration', checked)
          "
        >
          {{ $t('auth.checkboxes.legal_guardian_declaration') }}
        </BaseCheckbox>
      </div>

      <!-- Additional Fields for kids -->
      <div
        v-if="!isAdult && !hasPatients"
        class="relative z-0 mt-6 w-full px-2 sm:mt-0"
      >
        <p class="mb-10 text-2xl font-medium text-darkestGray">
          Wprowadź dane rodzica/opiekuna zamawiającego badania
        </p>
        <div class="flex flex-wrap">
          <!-- First Name -->
          <div class="z-1 relative mb-6 w-full px-2 lg:w-1/2">
            <BaseFormInput
              :value="guardianData.first_name"
              name="guardian_first_name"
              :label="$t('auth.first_name')"
              :any-dirty="guardianV$.first_name.$anyDirty"
              :errors="errors"
              :is-required="true"
              @update:value="
                (value) => updateGuardianValue('first_name', value)
              "
            />
          </div>

          <!-- Last Name -->
          <div class="z-1 relative mb-6 w-full px-2 lg:w-1/2">
            <BaseFormInput
              :value="guardianData.last_name"
              name="guardian_last_name"
              :label="$t('auth.last_name')"
              :any-dirty="guardianV$.last_name.$anyDirty"
              :errors="errors"
              :is-required="true"
              @update:value="(value) => updateGuardianValue('last_name', value)"
            />
          </div>
        </div>

        <div class="flex flex-wrap">
          <!-- Phone Number -->
          <div class="z-1 relative mb-6 w-full px-2 lg:w-1/2">
            <BasePhoneNumberInput
              name="guardian_national_phone_number"
              :phone-number="guardianData.national_phone_number"
              :country="guardianData.phone_number_country"
              :errors="errors"
              :is-required="true"
              @update="onUpdatePhoneNumber($event)"
            />
          </div>

          <!-- Email Address -->
          <div class="z-1 relative mb-6 w-full px-2 lg:w-1/2">
            <BaseFormInput
              :value="guardianData.email"
              name="guardian_email"
              type="email"
              :label="$t('auth.email')"
              :any-dirty="guardianV$.email.$anyDirty"
              :errors="errors"
              :is-required="true"
              @update:value="(value) => updateGuardianValue('email', value)"
            />
          </div>
        </div>

        <!-- Accept Terms 1 -->
        <BaseCheckbox
          id="guardian_accept_terms1"
          name="guardian_accept_terms1"
          :checked="guardianData.accept_terms1 ?? false"
          :required="true"
          theme="square"
          :errors="errors"
          class="text-sm"
          @change="updateGuardianAcceptTerms1"
        >
          {{ $t('auth.checkboxes.accept_terms1') }}
        </BaseCheckbox>

        <!-- Accept Terms 2 -->
        <BaseCheckbox
          id="guardian_accept_terms2"
          name="guardian_accept_terms2"
          :checked="guardianData.accept_terms2 ?? false"
          :required="true"
          theme="square"
          :errors="errors"
          class="text-sm"
          @change="updateGuardianAcceptTerms2"
        >
          {{ $t('auth.checkboxes.accept_terms2') }}
        </BaseCheckbox>
      </div>
    </div>

    <!-- Delete Button -->
    <div v-if="canDelete" class="w-10">
      <button
        class="transition-ease-in-out mt-4 shrink-0 hover:opacity-50"
        @click="onClickDelete"
      >
        <img
          src="~/assets/icons/base/close.svg"
          loading="lazy"
          alt="close-icon"
          class="shrink-0"
        />
      </button>
    </div>
  </div>
</template>

<script setup>
import useVuelidate from '@vuelidate/core'
import { required, requiredIf, email, helpers } from '@vuelidate/validators'
import { useCartStore } from '~~/stores/cart'

const { t } = useI18n()
const {
  isValidPesel,
  hasAgeBetween,
  hasAgeGreaterThan,
  hasPeselAgeBetween,
  hasPeselAgeGreaterThan,
} = useValidationsRules()

const props = defineProps({
  index: {
    type: [Number],
    required: true,
  },
  patient: {
    type: [Object],
    required: true,
  },
  canDelete: {
    type: [Boolean],
    required: true,
  },
})
const cartStore = useCartStore()

const sexOptions = computed(() => [
  { name: t('auth.gender.f'), value: 'f' },
  { name: t('auth.gender.m'), value: 'm' },
])

const errors = ref({})
const isValidated = ref(false)

const isAdult = computed(() => {
  return props.patient.age === 'adult'
})

const patients = computed(() => {
  return cartStore.patients.sort((p1, p2) => {
    if (p1.age < p2.age) return -1
    if (p1.age > p2.age) return 1
    return 0
  })
})

const hasPatients = computed(() => {
  return patients.value.length > 0
})

const updatePatientValue = (key, value) => {
  if (key === 'isNoPesel' && value) {
    cartStore.updateTmpPatient({
      id: props.patient.id,
      patient: {
        ...props.patient,
        [key]: value,
        ['pesel']: null,
      },
    })
  } else {
    cartStore.updateTmpPatient({
      id: props.patient.id,
      patient: {
        ...props.patient,
        [key]: value,
      },
    })
  }

  if (isValidated.value) {
    validate()
  }
}

const updateGuardianValue = (key, value) => {
  guardianData[key] = value
  if (isValidated.value) {
    validate()
  }
}

const isValidPhoneAdult = ref(false)
const isValidPhoneChild = ref(false)

const onUpdatePhoneNumber = (event) => {
  const { isValid, countryCode, countryCallingCode, nationalNumber } = event

  if (isAdult.value) {
    // adult
    cartStore.updateTmpPatient({
      id: props.patient.id,
      patient: {
        ...props.patient,
        country: countryCode,
        country_code: countryCallingCode,
        phone_number: nationalNumber,
      },
    })
    isValidPhoneAdult.value = isValid
  } else {
    // child
    guardianData.national_phone_number = nationalNumber
    guardianData.country = countryCode
    guardianData.phone_number_country = countryCode
    guardianData.phone_number_country_calling_code = countryCallingCode
    isValidPhoneChild.value = isValid
  }

  if (isValidated.value) {
    validate()
  }
}

const onClickDelete = () => {
  cartStore.deleteTmpPatient(props.patient.id)
}

const isDistinctPeselInPatients = (value) => {
  let quantity = 0

  quantity += cartStore.patients.filter(
    (patient) => patient.pesel === value,
  ).length
  quantity += cartStore.tmpPatients.filter(
    (patient) => patient.pesel === value,
  ).length

  return quantity <= 1
}

// Custom validator for accept terms that only runs when required
const acceptedIfRequired = (condition) => {
  return helpers.withMessage(t('error.accept_terms.accepted'), (value) => {
    if (!condition()) return true
    return value === true
  })
}

// Custom validator for phone number that only runs when required
const isValidPhoneIfRequired = (isValidPhoneRef, condition) => {
  return helpers.withMessage(t('error.phone_number.incorrect'), () => {
    if (!condition()) return true
    return isValidPhoneRef.value
  })
}

// Make patientData a computed property to ensure reactivity
const patientData = computed(() => props.patient)

const patientDataRules = computed(() => ({
  first_name: {
    required: helpers.withMessage(t('error.first_name.required'), required),
  },
  last_name: {
    required: helpers.withMessage(t('error.last_name.required'), required),
  },
  pesel: {
    required: helpers.withMessage(
      t('error.pesel.required'),
      requiredIf(() => !patientData.value.isNoPesel),
    ),
    invalid: helpers.withMessage(
      t('error.pesel.invalid'),
      (value) => patientData.value.isNoPesel || isValidPesel(value),
    ),
    isKidOnAdultForm: helpers.withMessage(
      t('error.pesel.change_to_kid'),
      (value) =>
        patientData.value.isNoPesel ||
        patientData.value.age === 'kid' ||
        (patientData.value.age === 'adult' &&
          hasPeselAgeGreaterThan(value, 216)),
    ),
    isAdultOnKidForm: helpers.withMessage(
      t('error.pesel.change_to_adult'),
      (value) =>
        patientData.value.isNoPesel ||
        patientData.value.age === 'adult' ||
        (patientData.value.age === 'kid' && hasPeselAgeBetween(value, 3, 216)),
    ),
    distinct: helpers.withMessage(
      t('error.pesel.already_taken'),
      (value) =>
        patientData.value.isNoPesel || isDistinctPeselInPatients(value),
    ),
  },
  sex: {
    required: helpers.withMessage(
      t('error.sex.required'),
      requiredIf(() => patientData.value.isNoPesel),
    ),
  },
  birth_date: {
    required: helpers.withMessage(
      t('error.birth_date.required'),
      requiredIf(() => patientData.value.isNoPesel),
    ),
    invalid_date: helpers.withMessage(
      t('error.birth_date.invalid_date'),
      (value) => {
        if (!patientData.value.isNoPesel) {
          return true // Skip validation if isNoPesel is false
        }

        if (patientData.value.age === 'kid') {
          return hasAgeBetween(value, 3, 216)
        } else if (patientData.value.age === 'adult') {
          return hasAgeGreaterThan(value, 216)
        }

        return true // Default to true if age is not specified
      },
    ),
  },
  phone_number: {
    required: helpers.withMessage(
      t('error.phone_number.required'),
      requiredIf(() => isAdult.value),
    ),
    isValidPhone: isValidPhoneIfRequired(
      isValidPhoneAdult,
      () => isAdult.value,
    ),
  },
  accept_data_processing: {
    required: helpers.withMessage(
      t('error.accept_terms.accepted'),
      requiredIf(() => !hasPatients.value && isAdult.value),
    ),
    accepted: acceptedIfRequired(() => !hasPatients.value && isAdult.value),
  },
  accept_terms: {
    required: helpers.withMessage(
      t('error.accept_terms.accepted'),
      requiredIf(() => !hasPatients.value && isAdult.value),
    ),
    accepted: acceptedIfRequired(() => !hasPatients.value && isAdult.value),
  },
  is_legal_guardian_declaration: {
    required: helpers.withMessage(
      t('error.accept_terms.accepted'),
      requiredIf(() => !isAdult.value),
    ),
    accepted: acceptedIfRequired(() => !isAdult.value),
  },
}))

// Validation rules for guardianData
const guardianDataRules = {
  first_name: {
    required: helpers.withMessage(t('error.first_name.required'), required),
  },
  last_name: {
    required: helpers.withMessage(t('error.last_name.required'), required),
  },
  national_phone_number: {
    required: helpers.withMessage(t('error.phone_number.required'), required),
    isValidPhone: isValidPhoneIfRequired(isValidPhoneChild, () => true),
  },
  email: {
    required: helpers.withMessage(t('error.email.required'), required),
    email: helpers.withMessage('Invalid email', email),
  },
  accept_terms1: {
    required: helpers.withMessage(t('error.accept_terms.accepted'), required),
    accepted: acceptedIfRequired(() => true),
  },
  accept_terms2: {
    required: helpers.withMessage(t('error.accept_terms.accepted'), required),
    accepted: acceptedIfRequired(() => true),
  },
}

const guardianData = reactive({
  first_name: '',
  last_name: '',
  national_phone_number: '',
  email: '',
  country: 'PL',
  phone_number_country: 'PL',
  phone_number_country_calling_code: '',
  isWithoutRegister: true,
  accept_terms1: false,
  accept_terms2: false,
})

const minBirthDate = computed(() => {
  const date = new Date()
  date.setFullYear(date.getFullYear() - 18)
  return date
})

const maxBirthDate = computed(() => {
  const date = new Date()
  if (isAdult.value) {
    date.setFullYear(date.getFullYear() - 18)
  } else {
    date.setMonth(date.getMonth() - 3)
  }
  return date
})

const patientV$ = useVuelidate(patientDataRules, patientData)
const guardianV$ = useVuelidate(guardianDataRules, guardianData)

const validate = async () => {
  isValidated.value = true
  errors.value = {}
  let isValid = await patientV$.value.$validate()

  // Handle patient data validation errors
  if (!isValid) {
    patientV$.value.$errors.forEach((error) => {
      if (!errors.value[error.$property]) {
        errors.value[error.$property] = error.$message
      }
    })
  }

  // Conditionally validate guardianData if the patient is a child and there are no other patients
  if (!isAdult.value && !hasPatients.value) {
    const guardianValid = await guardianV$.value.$validate()

    if (!guardianValid) {
      guardianV$.value.$errors.forEach((error) => {
        // Prefix with 'guardian_' to match input names
        const errorKey = `guardian_${error.$property}`
        if (!errors.value[errorKey]) {
          errors.value[errorKey] = error.$message
        }
      })
      isValid = false
    }
  }

  return isValid
}

// Load guardianData from sessionStorage if available
// onMounted(() => {
//   const savedData = sessionStorage.getItem('guardian_data')
//   if (savedData) {
//     Object.assign(personalData, JSON.parse(savedData))
//   }
// })

// Watch guardianData and save to sessionStorage on changes
watch(
  guardianData,
  (newData) => {
    sessionStorage.setItem('personal_data', JSON.stringify(newData))
  },
  { deep: true },
)

defineExpose({ validate })

// Handler functions for guardianData checkboxes

const updateGuardianAcceptTerms1 = (checked) => {
  updateGuardianValue('accept_terms1', checked)
}

const updateGuardianAcceptTerms2 = (checked) => {
  updateGuardianValue('accept_terms2', checked)
}
</script>

<style scoped>
/* Add your component-specific styles here */
</style>
